import type { MarkdownEditorContextValue } from './markdown-editor-context';
import type { NodeViewProps } from '@tiptap/react';
import type { MutableRefObject } from 'react';

import { Node } from '@tiptap/core';
import '@tiptap/markdown';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

import { DocumentTitleBlock } from '#components/document-viewer/document-title/document-title-block';
import { normalizeDocumentTitleConfig } from '#components/document-viewer/document-title/document-title-config';

import { createDocumentTitleTag } from './markdown-document-title';

interface MarkdownDocumentTitleOptions {
  contextRef: MutableRefObject<MarkdownEditorContextValue>;
}

function MarkdownDocumentTitleNodeView({
  extension,
  selected,
  node,
}: NodeViewProps) {
  const { contextRef } = extension.options as MarkdownDocumentTitleOptions;
  const context = contextRef.current;
  const config = normalizeDocumentTitleConfig({
    explicitTitle:
      typeof node.attrs?.explicitTitle === 'string'
        ? node.attrs.explicitTitle
        : null,
  });

  return (
    <NodeViewWrapper
      className={
        selected
          ? 'markdown-document-title-node markdown-document-title-node--selected'
          : 'markdown-document-title-node'
      }
      contentEditable={false}
      data-drag-handle={false}
    >
      <DocumentTitleBlock
        config={config}
        parentPath={context.parentPath}
        children={context.childDocuments}
        editorPlaceholderMuted={context.editorPlaceholderMuted}
        isNewDocument={context.isNewDocument}
        onCreateSubpage={context.onCreateSubpage}
        onNavigateToDocument={context.onNavigateToDocument}
        onRequestRename={context.onRequestRename}
      />
    </NodeViewWrapper>
  );
}

export const MarkdownDocumentTitleNode =
  Node.create<MarkdownDocumentTitleOptions>({
    name: 'npDocumentTitle',
    group: 'block',
    atom: true,
    selectable: true,
    isolating: true,
    defining: true,

    addOptions() {
      return {
        contextRef: { current: null as never },
      };
    },

    addAttributes() {
      return {
        explicitTitle: {
          default: null,
        },
      };
    },

    parseHTML() {
      return [
        {
          tag: 'np-document-title',
          getAttrs: (element) => {
            if (!(element instanceof HTMLElement)) return false;

            const explicitTitle = element.getAttribute('data-title');
            return {
              explicitTitle: explicitTitle?.trim() || null,
            };
          },
        },
      ];
    },

    renderHTML({ HTMLAttributes }) {
      const explicitTitle =
        typeof HTMLAttributes.explicitTitle === 'string'
          ? HTMLAttributes.explicitTitle.trim()
          : '';

      return explicitTitle
        ? ['np-document-title', { 'data-title': explicitTitle }]
        : ['np-document-title'];
    },

    renderMarkdown(node) {
      return createDocumentTitleTag(
        typeof node?.attrs?.explicitTitle === 'string'
          ? node.attrs.explicitTitle
          : null,
      );
    },

    addNodeView() {
      return ReactNodeViewRenderer(MarkdownDocumentTitleNodeView);
    },
  });
